import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, errorMessage } from '../../api/client';
import { formatCents } from '../../lib/money';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';
import { AttemptStatusBadge } from '../../components/payments/PaymentActivity';
import { useAuth } from '../../providers/AuthProvider';

/**
 * 0.26.0 — "Money in flight": online bank payments that are processing (submitted, NOT yet paid) or
 * waiting on the tenant, plus abandoned attempts. Owners can pull from Stripe (sync) and check that
 * the Stripe webhook is subscribed to every required event.
 */
function MoneyInFlight({ isOwner }: { isOwner: boolean }) {
  const queryClient = useQueryClient();
  const summary = useQuery({ queryKey: ['admin', 'payment-summary'], queryFn: () => api.paymentAttempts.summary() });
  const sync = useMutation({
    mutationFn: () => api.paymentAttempts.sync(),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'payment-summary'] });
      void queryClient.invalidateQueries({ queryKey: ['admin', 'payment-attempts'] });
    },
  });
  const status = useMutation({ mutationFn: () => api.paymentAttempts.stripeStatus() });

  if (summary.isLoading) return <Loading label="Loading payment activity…" />;
  if (summary.isError || !summary.data)
    return <ErrorState message={errorMessage(summary.error)} onRetry={() => void summary.refetch()} />;
  const s = summary.data;

  return (
    <section aria-labelledby="inflight-heading" className="stack">
      <h2 id="inflight-heading">Online payments</h2>
      <div className="grid">
        <div className="stat">
          <p className="stat-label">Money in flight (processing)</p>
          <p className="stat-value">{formatCents(s.processing.totalCents)}</p>
          <p className="muted" style={{ margin: 0 }}>
            {s.processing.count} payment{s.processing.count === 1 ? '' : 's'} — not yet paid
            {s.processing.latestExpectedSettlementDate
              ? `; expected to clear by ${formatDate(s.processing.latestExpectedSettlementDate)} (estimate)`
              : ''}
          </p>
        </div>
        <div className="stat">
          <p className="stat-label">Waiting on tenant</p>
          <p className="stat-value">{s.requiresAction.count}</p>
          <p className="muted" style={{ margin: 0 }}>
            {formatCents(s.requiresAction.totalCents)} — e.g. bank verification; no money moved
          </p>
        </div>
        <div className="stat">
          <p className="stat-label">Abandoned payment attempts</p>
          <p className="stat-value">{s.abandoned.count}</p>
          <p className="muted" style={{ margin: 0 }}>
            Started, never submitted ({formatCents(s.abandoned.totalCents)})
          </p>
        </div>
        <div className="stat">
          <p className="stat-label">Failed (30 days)</p>
          <p className="stat-value">{s.failedRecent.count}</p>
        </div>
      </div>

      {s.inFlight.length > 0 && (
        <div className="table-wrap card" style={{ padding: 0 }}>
          <table>
            <caption className="visually-hidden">Payments in flight</caption>
            <thead>
              <tr>
                <th scope="col">Tenant</th>
                <th scope="col">Home</th>
                <th scope="col" className="num">
                  Amount
                </th>
                <th scope="col">Status</th>
                <th scope="col">Expected (estimate)</th>
              </tr>
            </thead>
            <tbody>
              {s.inFlight.map((a) => (
                <tr key={a.id}>
                  <td>{a.tenantName ?? '—'}</td>
                  <td>
                    <Link to={`/admin/leases/${encodeURIComponent(a.leaseId)}`}>
                      {[a.propertyName, a.unitLabel ? `Unit ${a.unitLabel}` : null].filter(Boolean).join(' — ') || 'Lease'}
                    </Link>
                  </td>
                  <td className="num">{formatCents(a.amountCents)}</td>
                  <td>
                    <AttemptStatusBadge status={a.status} />
                  </td>
                  <td>{a.expectedSettlementDate ? formatDate(a.expectedSettlementDate) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {isOwner && (
        <div className="stack">
          <div className="row" style={{ flexWrap: 'wrap' }}>
            <button type="button" className="secondary" disabled={sync.isPending} onClick={() => sync.mutate()}>
              {sync.isPending ? 'Syncing…' : 'Sync from Stripe (last 90 days)'}
            </button>
            <button type="button" className="secondary" disabled={status.isPending} onClick={() => status.mutate()}>
              {status.isPending ? 'Checking…' : 'Check Stripe webhook events'}
            </button>
          </div>
          {sync.isError && <StatusMessage tone="err">{errorMessage(sync.error)}</StatusMessage>}
          {sync.data && (
            <StatusMessage tone="ok">
              Checked {sync.data.matched} payment{sync.data.matched === 1 ? '' : 's'} for this account: {sync.data.created} new,{' '}
              {sync.data.updated} updated, {sync.data.unchanged} unchanged
              {sync.data.skipped ? `, ${sync.data.skipped} skipped (lease not found)` : ''}.
            </StatusMessage>
          )}
          {status.isError && <StatusMessage tone="err">{errorMessage(status.error)}</StatusMessage>}
          {status.data &&
            (status.data.ok ? (
              <StatusMessage tone="ok">The Stripe webhook is subscribed to every required event.</StatusMessage>
            ) : (
              <StatusMessage tone="warn">
                {!status.data.configured
                  ? 'Stripe is not configured.'
                  : status.data.endpoints.some((e) => e.matchesThisApp)
                    ? `The Stripe webhook is missing: ${
                        [...new Set(status.data.endpoints.filter((e) => e.matchesThisApp).flatMap((e) => e.missingEvents))].join(', ') ||
                        '(endpoint disabled)'
                      }. Add them in Stripe → Developers → Webhooks → your endpoint → Select events.`
                    : `No Stripe webhook points at ${status.data.expectedUrls[0] ?? 'this app'}.`}
              </StatusMessage>
            ))}
        </div>
      )}
    </section>
  );
}

/** Owner/staff landing: portfolio counts + money in flight + quick links into the management shells. */
export function AdminDashboard() {
  const props = useQuery({ queryKey: ['admin', 'properties'], queryFn: () => api.properties.list() });
  const leases = useQuery({ queryKey: ['admin', 'leases'], queryFn: () => api.leases.list() });
  const { principal } = useAuth();

  if (props.isLoading || leases.isLoading) return <Loading label="Loading portfolio…" />;
  if (props.isError) return <ErrorState message={errorMessage(props.error)} onRetry={() => props.refetch()} />;
  if (leases.isError) return <ErrorState message={errorMessage(leases.error)} onRetry={() => leases.refetch()} />;

  return (
    <>
      <h1>Portfolio dashboard</h1>
      <p className="subtitle">Manage your properties, leases, and payment methods.</p>

      <div className="grid">
        <div className="stat">
          <p className="stat-label">Properties</p>
          <p className="stat-value">{props.data?.properties.length ?? 0}</p>
          <p className="muted" style={{ margin: 0 }}>
            <Link to="/admin/properties">Manage</Link>
          </p>
        </div>
        <div className="stat">
          <p className="stat-label">Leases</p>
          <p className="stat-value">{leases.data?.leases.length ?? 0}</p>
          <p className="muted" style={{ margin: 0 }}>
            <Link to="/admin/leases">View</Link>
          </p>
        </div>
        <div className="stat">
          <p className="stat-label">Payment methods</p>
          <p className="stat-value">§1947.3</p>
          <p className="muted" style={{ margin: 0 }}>
            <Link to="/admin/payment-methods">Configure</Link>
          </p>
        </div>
      </div>

      <MoneyInFlight isOwner={principal?.role === 'owner'} />
    </>
  );
}
